import React from 'react';
import styled from '@emotion/styled';
import { OMGTheme } from '@ordermygear/theme';
import Moment from 'moment-timezone';
import { Sale, Dealer, Order, ShipmentTracker } from '../../../../backend/shared/types';
import OrderTracker from './OrderTracker';

const Container = styled.div`
  width: 100%;
`;

const PanelContainer = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-column-gap: 20px;
  padding-top: 40px;
  padding-left: 0;
  padding-right: 0;
  h2 {
    font-size: 36px;
    font-weight: 500;
    margin-bottom: 20px;
  }
  h2 span {
    font-size: 30px;
  }
  dt {
    font-size: 22px;
    font-weight: normal; 
    margin-bottom: 5px;  
    font-weight: 500;
  }
  dd {
    font-size: 18px;
    font-weight: normal;
    margin: 0;
    line-height: 25px;
  }
  dl {
    margin-bottom: 20px;
  }
  @media only screen and (max-width: ${OMGTheme.breakpoints.xs}) {
    padding-bottom: 15px;
    padding-top: 15px;
    display: block;
    h2 {
      font-size: 18px;
    }
    h2 span {
      font-size: 18px;
    }
    dt {
      font-size: 14px;
    }
    dd {
      font-size: 14px;
    }
  }
`;

const RightPanel = styled.div`
  text-align: right;
  @media only screen and (max-width: ${OMGTheme.breakpoints.xs}) {
    text-align: left;
  }
`;

const LeftPanel = styled.div`
  text-align: left;
  @media only screen and (max-width: ${OMGTheme.breakpoints.xs}) {
    text-align: left;
  }
`;

const TrackingContainer = styled.div`
  width: 100%; 
  border-bottom: 1px solid ${OMGTheme.colors.grayLightBorder};
  padding-bottom: 40px;
  h4 {
    font-size: 22px;
    font-weight: normal; 
    margin-bottom: 5px;  
    font-weight: 500;
  }
`;

const TrackingList = styled.ul`
  list-style-type: none;
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
`;

type OrderInfoProps = {
  sale: Sale;
  order: Order;
  dealer: Dealer;
}

const OrderInfo = (props: OrderInfoProps) => {
  const { sale, order, dealer } = props;
  const {
    id,
    customer,
    settled_on,
    shipping_address,
    shipment_trackers
  } = order;
  const countryMap: any = {
    'US': 'United States',
    'CA': "Canada",
    'MX': "Mexico"
  }
  const settledOnTz = settled_on ? Moment(settled_on).tz(dealer.timezone) : null;
  return (
    <Container>
      <PanelContainer>
        <LeftPanel>
          <h2>Order Number <span>#{id}</span></h2>
          <dl>
            <dt>Contact Information</dt>
            <dd>Phone: {customer.phone}
            <br />
            Email: {customer.email}</dd>
          </dl>
          <dl>
            {shipping_address && shipping_address.first_name ?
              <>
                <dt>Shipping Address</dt>
                <dd>
                  {shipping_address.first_name} {shipping_address.last_name}
                  <br />
                  {shipping_address.line1}
                  <br />
                  {shipping_address.line2 ? <>{shipping_address.line2}<br /></> : <React.Fragment />}
                  {shipping_address.city}, {shipping_address.state} {shipping_address.postal_code}
                  <br />
                  {countryMap[shipping_address.country_code]}
                </dd>
              </> : <React.Fragment />}
          </dl>
        </LeftPanel>
        <RightPanel>
          <dl>
            <dt>
              Order Date
            </dt>
            <dd>
              {settledOnTz ? settledOnTz.format('MMM. Do, y') : 'N/A'}
            </dd>
          </dl>
        </RightPanel>
      </PanelContainer>
      <TrackingContainer>
      {shipment_trackers?.length ?
        <dl>
          <h4>Tracking Information</h4>
          <TrackingList>
            {shipment_trackers.map((tracker: ShipmentTracker, index) => {
              return (
                <OrderTracker
                  carrier={tracker.carrier}
                  code={tracker.code}
                  key={index}
                />
              );
            })}
          </TrackingList>
        </dl> :
        <React.Fragment />
      }
      </TrackingContainer>
    </Container>
  );
};

export default OrderInfo;